import {useEffect, useMemo, useState} from 'react';
import {Modal} from 'react-bootstrap';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
  authCompanyData,
  clearCompanyData,
  clearPublicUser,
  redirectPublicUser,
} from '../../redux/actions';
import {BASE_URL_WEB} from '../../utility/constants';
import {AxiosHelperPortal, calcClassRemainingSpots, showToast} from '../../utility/helperFunctions';
import './styles.css';

const WaitlistActions = ({details, isFull: isFullProp, compact = false}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const auth = useSelector(state => state.auth);
  const publicUser = useSelector(state => state.publicUser);
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState('join');
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState({
    isWaitlisted: Boolean(details?.isWaitlisted),
    waitlistedParticipantIds: details?.waitlistedParticipantIds || [],
    waitlistedParticipants: details?.waitlistedParticipants || [],
  });
  const loggedIn = Boolean(auth?.authDetails);
  const member = auth?.companyData?.member;
  const isFull =
    typeof isFullProp === 'boolean'
      ? isFullProp
      : calcClassRemainingSpots(details) === 'Class Full';
  const detailButtonStyle = compact
    ? undefined
    : {position: 'static', transform: 'none', margin: '0'};
  const isManageMode = mode === 'manage';

  useEffect(() => {
    setSummary({
      isWaitlisted: Boolean(details?.isWaitlisted),
      waitlistedParticipantIds: details?.waitlistedParticipantIds || [],
      waitlistedParticipants: details?.waitlistedParticipants || [],
    });
  }, [details?.isWaitlisted, details?.waitlistedParticipantIds, details?.waitlistedParticipants]);

  useEffect(() => {
    if (
      loggedIn &&
      publicUser?.waitlistIntent === 'join' &&
      publicUser?.classId === details?.uuid
    ) {
      setMode('join');
      setSelected([]);
      setShow(true);
    }
  }, [loggedIn, publicUser?.waitlistIntent, publicUser?.classId, details?.uuid]);

  const participants = useMemo(() => {
    const primary = member
      ? [{uuid: '_parent', firstName: member.firstName, lastName: member.lastName, isParent: true}]
      : [];
    return [...primary, ...(auth?.companyData?.familyMembers || [])];
  }, [auth?.companyData?.familyMembers, member]);

  const bookedIds = useMemo(
    () =>
      (details?.familyMembers || []).map(participant =>
        participant?.isParent ? '_parent' : participant?.id || participant?.uuid
      ),
    [details?.familyMembers]
  );

  const availableParticipants =
    mode === 'manage'
      ? participants.filter(participant => {
          const id = participant.isParent ? member?.id : participant.uuid;
          const alreadyWaitlisted = summary.waitlistedParticipantIds.includes(id);
          return alreadyWaitlisted || (isFull && !bookedIds.includes(participant.uuid));
        })
      : participants.filter(participant => {
          const id = participant.isParent ? member?.id : participant.uuid;
          return (
            !bookedIds.includes(participant.uuid) && !summary.waitlistedParticipantIds.includes(id)
          );
        });
  const primaryActionDisabled =
    submitting || availableParticipants.length === 0 || (!isManageMode && selected.length === 0);

  const open = nextMode => {
    if (!loggedIn) {
      dispatch(clearCompanyData());
      dispatch(
        redirectPublicUser({
          companyId: auth?.companyData?.id,
          companyName: auth?.companyData?.name,
          classId: details?.uuid,
          branchDetails: auth?.companyData?.branches?.items,
          imageUrl: auth?.companyData?.imageUrl,
          showKidsAfterSignup: auth?.companyData?.showKidsAfterSignup,
          eventDate: details?.start_branch_time,
          haveKids: details?.haveKids,
          bookingType: details?.isCamp ? 'camp' : 'class',
          waitlistIntent: 'join',
        })
      );
      history.push('/login');
      return;
    }
    setMode(nextMode);
    setSelected(nextMode === 'manage' ? [...summary.waitlistedParticipantIds] : []);
    setShow(true);
  };

  const toggle = participant => {
    const id = participant.isParent ? member?.id : participant.uuid;
    setSelected(current =>
      current.includes(id) ? current.filter(value => value !== id) : [...current, id]
    );
  };

  const submit = async () => {
    if (mode !== 'manage' && selected.length === 0) {
      showToast.error('Please select at least one participant.');
      return;
    }
    setSubmitting(true);
    try {
      let operations = [{action: mode, participantIds: selected}];
      if (mode === 'manage') {
        operations = [
          {
            action: 'leave',
            participantIds: summary.waitlistedParticipantIds.filter(id => !selected.includes(id)),
          },
          {
            action: 'join',
            participantIds: selected.filter(id => !summary.waitlistedParticipantIds.includes(id)),
          },
        ].filter(operation => operation.participantIds.length > 0);
      }
      if (operations.length === 0) {
        setShow(false);
        return;
      }
      let response;
      for (const operation of operations) {
        response = await AxiosHelperPortal(`${BASE_URL_WEB}waitlist/${operation.action}?`, 'post', {
          classId: details.uuid,
          participantIds: operation.participantIds,
        });
        setSummary(response.data.waitlist);
      }
      showToast.success(
        mode === 'manage' ? 'Your wait list selections have been updated.' : response.data.message
      );
      setShow(false);
      dispatch(clearPublicUser());
      dispatch(authCompanyData());
    } catch (error) {
      showToast.error(
        error?.data?.message || error?.response?.data?.message || 'Unable to update the wait list.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (details?.status === 'cancelled' || (!isFull && !summary.isWaitlisted)) return null;

  return (
    <>
      {summary.isWaitlisted ? (
        <button
          className={`btn ${compact ? 'view-det' : '_btnRound'} mr-1`}
          style={detailButtonStyle}
          onClick={() => open('manage')}
        >
          Manage Wait List
        </button>
      ) : (
        <button
          className={`btn ${compact ? 'view-det' : '_btnRound'} mr-1`}
          style={detailButtonStyle}
          onClick={() => open('join')}
        >
          Join Wait List
        </button>
      )}

      <Modal
        show={show}
        onHide={() => setShow(false)}
        centered
        className="waitlist-picker-modal"
        dialogClassName="waitlist-picker-dialog"
        backdropClassName="waitlist-picker-backdrop"
        aria-labelledby="waitlist-picker-title"
        aria-describedby="waitlist-picker-description"
      >
        <div className="modal-header waitlist-picker-header">
          <div>
            <h5 className="modal-title" id="waitlist-picker-title">
              {mode === 'join' ? 'Join Wait List' : 'Manage Wait List'}
            </h5>
            <p className="waitlist-picker-subtitle mb-0" id="waitlist-picker-description">
              {isManageMode
                ? 'Choose who should remain on the wait list.'
                : 'Select one or more participants to add.'}
            </p>
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setShow(false)}
          />
        </div>
        <div className="modal-body waitlist-picker-body">
          {availableParticipants.length === 0 ? (
            <div className="waitlist-picker-empty">
              There are no eligible participants to {mode === 'join' ? 'add' : 'manage'}.
            </div>
          ) : (
            <div className="waitlist-participant-list">
              {availableParticipants.map(participant => {
                const id = participant.isParent ? member?.id : participant.uuid;
                const checked = selected.includes(id);
                return (
                  <label
                    key={id}
                    className={`waitlist-participant-option${checked ? ' is-selected' : ''}`}
                  >
                    <input type="checkbox" checked={checked} onChange={() => toggle(participant)} />
                    <span className="waitlist-participant-name">
                      {participant.firstName} {participant.lastName}
                    </span>
                    {participant.isParent && (
                      <span className="waitlist-participant-badge">Primary member</span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div className="modal-footer waitlist-picker-footer">
          <span className="waitlist-selection-count">
            {selected.length} {selected.length === 1 ? 'participant' : 'participants'} selected
          </span>
          <div className="waitlist-picker-footer-actions">
            <button
              type="button"
              className="btn waitlist-cancel-button"
              onClick={() => setShow(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn waitlist-primary-button"
              disabled={primaryActionDisabled}
              onClick={submit}
            >
              {submitting ? 'Saving…' : mode === 'join' ? 'Join Wait List' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default WaitlistActions;
